import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  DateRangePickerProps,
  TableProps,
  ExpandableSection,
} from '@cloudscape-design/components';

import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUrlFilterState, useUrlRangeState } from '../../common/filtering/filters';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { buildFiltering } from '../../common/filtering/places';
import { ResourceError } from '../../components/Loading';
import { exportExcel } from '../../common/excel';
import { PropertyFilterOption, PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { Place, PlaceContact } from '../../common/types';
import { getPlaceMeta } from '../../common/api/places';
import { getPlaces } from '../../common/api/places';
import { AddToListModal } from '../AddToListModal';
import { PlaceNameCell } from '../PlaceNameCell';
import { PlaceContactsList } from '../PlaceContactsList';
import { PlaceReviewsInfo } from '../PlaceReviewsInfo';
import { TextQueryFilter } from '../TextQueryFilter';
import { LatestReportInfo } from '../LatestReportInfo';

export type PlaceTableProps = {
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideActions?: boolean;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  additionalActions?: React.ReactNode;
  selectedItems?: Place[];
  setSelectedItems?: (items: Place[]) => void;
  textQuery?: string;
  setTextQuery?: (text: string) => void;
  // --- Reuse hooks: render this same table for a scoped subset (e.g. a
  // chain's locations) by injecting a different data source. Defaults keep
  // the global /dashboard/places behavior byte-for-byte. ---
  /** Data fetcher; defaults to getPlaces. Same signature/return shape. */
  fetchData?: typeof getPlaces;
  /** Filter-meta fetcher; defaults to getPlaceMeta. */
  fetchMeta?: typeof getPlaceMeta;
  /** Hide the property/date "Additional filters" (keeps the text filter).
   *  Use for scoped sources whose endpoint only supports text search. */
  hideAdditionalFilters?: boolean;
  /** Query-cache namespace; defaults to 'places'. Set per scope so a
   *  chain's list doesn't share cache with the global places list. */
  endpointName?: string;
  /** Header title; defaults to 'Places'. */
  resourceName?: string;
  /** Header description text. */
  description?: string;
  /** Hide the Excel export button (e.g. embedded chain view). */
  hideExport?: boolean;
  /** Extra filter controls rendered next to the text filter (e.g. the
   *  chain-scoped business type / industry / source selects). */
  extraFilters?: React.ReactNode;
};

export function PlaceTable(props: PlaceTableProps) {
  const {
    now,
    setNow,
    query,
    setQuery,
    range,
    setRange,
    selectedItems,
    setSelectedItems,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
    textQuery,
    setTextQuery,
  } = props;
  const navigate = useNavigate();
  const fetchData = props.fetchData ?? getPlaces;
  const fetchMeta = props.fetchMeta ?? getPlaceMeta;
  const hideAdditionalFilters = props.hideAdditionalFilters ?? false;
  const resourceName = props.resourceName ?? 'Places';
  const endpointName = props.endpointName ?? 'places';
  // relative times are in the past vs future
  const past = true;

  // TODO move to context
  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: fetchMeta,
    staleTime: 360_000, // 10 minutes
    // Scoped views that hide the property filters don't need meta.
    enabled: !hideAdditionalFilters,
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [
      endpointName,
      query,
      range,
      pageSize,
      sortingField,
      sortDirection,
      page,
      now,
      textQuery,
    ],
    // Scoped views (no meta) fetch immediately; the global view waits for meta.
    enabled: hideAdditionalFilters || metaQuery.isSuccess,
    // TODO add sorting
    queryFn: async () =>
      await fetchData(query, page, pageSize, range, sortingField, sortDirection, now, textQuery),
    // 1 minute
    staleTime: 60_000,
    // placeholderData: keepPreviousData,
  });

  const loading =
    (isPending ||
      isFetching ||
      (!hideAdditionalFilters && (metaQuery.isPending || metaQuery.isFetching))) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <Table
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{
        sortingField,
      }}
      // default to ascending
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          description={
            props.description ?? 'View and manage places from which reviews are collected.'
          }
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                {props.additionalActions}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                {!props.hideExport && (
                  <Button
                    disabled={loading}
                    iconName="download"
                    onClick={() => {
                      exportExcel(
                        (data?.items ?? []).map((item) => ({
                          name: item.name,
                          address: item.address,
                          rating: item.rating,
                          reviews: item.total_review_count,
                          contacts: item.contacts
                            .filter((contact) => contact.phone_number || contact.email)
                            .map(
                              (contact) =>
                                `${contact.first_name} ${contact.last_name}: ${contact.phone_number ?? contact.email}`,
                            )
                            .join('\n'),
                        })),
                        'places',
                      );
                    }}
                  />
                )}
              </SpaceBetween>
            )
          }
          counter={
            data?.total != null
              ? metaQuery.data?.total != null
                ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
                : `(${data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      items={data?.items ?? []}
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{
            options: PAGE_SIZE_OPTIONS,
          }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <SpaceBetween direction="vertical" size="xxxs">
            <SpaceBetween direction="horizontal" size="xs">
              <TextQueryFilter
                loading={loading}
                text={textQuery ?? ''}
                setText={(q) => {
                  setTextQuery?.(q);
                  // reset page when filtering
                  setPage(1);
                }}
              />
              {props.extraFilters}
            </SpaceBetween>
            {hideAdditionalFilters ? null : (
            <ExpandableSection headerText="Additional filters">
              <PropertyDateFilter
                loading={loading}
                query={query}
                setQuery={(q) => {
                  setQuery?.(q);
                  // reset page when filtering
                  setPage(1);
                }}
                range={range ?? null}
                setRange={(r) => {
                  setRange?.(r);
                  // reset page when filtering
                  setPage(1);
                }}
                propertyFilteringOptions={options}
                filteringProperties={properties}
                past={past}
              />
            </ExpandableSection>
            )}
          </SpaceBetween>
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          sortingField: 'rating',
          minWidth: 220,
          cell: (item: Place) => <PlaceNameCell place={item} />,
        },
        {
          id: 'scorecard',
          header: 'Scorecard',
          cell: (item: Place) => <LatestReportInfo reports={item.reports} placeName={item.name} />,
        },
        // {
        //   id: 'phone',
        //   header: 'Phone',
        //   cell: (item) => `${item.phone_number ?? 'Unknown'}`,
        // },
        // {
        //   id: 'rating',
        //   header: 'Rating',
        //   cell: (item: Place) => `${item.rating ?? 'Unknown'}`,
        //   sortingField: 'rating',
        // },
        // replace with scorecard
        // {
        //   id: 'reviews',
        //   header: 'Reviews',
        //   cell: (item: Place) => <PlaceReviewsInfo place={item} />,
        //   sortingField: 'total_review_count',
        // },
        {
          id: 'contacts',
          header: 'Google Contacts',
          // sortingField: 'contacts',
          cell: (item: Place) => {
            return <PlaceContactsList contacts={item.contacts} />;
          },
        },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
  );
}
